import { h, Host, Component, Prop, Event, EventEmitter } from '@stencil/core';
import state, { store } from '../../state';
import { getViewportValue } from '../../utils';

import type { NotificationContent, NotificationSubmitEventArg } from '../../types';

const fieldMapping: Record<'primary' | 'secondary' | 'tertiary', 'cta' | 'secondCta' | 'thirdCta'> = {
  primary: 'cta',
  secondary: 'secondCta',
  tertiary: 'thirdCta',
};

@Component({
  tag: 'om-notification-screen',
  styleUrl: './notification-screen.css',
})
export class NotificationScreen {
  @Prop() screenValues: NotificationContent;
  @Prop() handleAction: (d: NotificationSubmitEventArg) => void;
  @Prop() unmount?: () => void;

  @Event() layoutContainerClicked: EventEmitter;

  private handleCtaClick(ctaType: 'primary' | 'secondary' | 'tertiary', ctaData: any) {
    if (state.mode === 'preview') return;

    const url = getViewportValue(ctaData, 'url');
    const target = getViewportValue(ctaData, 'target') || '_blank';
    const ctaHtml = getViewportValue(ctaData, 'text'); // HTML string — strip tags to get plain-text label for tracking
    const div = document.createElement('div');
    div.innerHTML = (ctaHtml || '')
      .replace(/<br\s*\/?>/gi, ' ')
      .replace(/<\/(?:p|div|h[1-6]|li|td|th|tr|blockquote)>/gi, ' ');
    const ctaLabel = (div.textContent || '').replace(/[\n\r\t]+/g, ' ').trim();

    const closeOverlay = getViewportValue(ctaData, 'closeOverlay');

    if (url) {
      // Emit before navigating — a same-tab open would otherwise cancel the tracking beacon
      this.handleAction({
        ctaLabel,
        ctaType,
        url,
        target,
      });

      window.open(url, target);
    }

    if (closeOverlay) {
      this.unmount?.();
    }
  }

  private renderCta(ctaData: any, ctaType: 'primary' | 'secondary' | 'tertiary') {
    const ctaHtml = getViewportValue(ctaData, 'text');
    const backgroundColour = getViewportValue(ctaData, 'backgroundColour');
    const borderRadius = getViewportValue(ctaData, 'borderRadius');
    const borderColour = getViewportValue(ctaData, 'borderColour');
    const borderWidth = getViewportValue(ctaData, 'borderWidth');

    return (
      <om-clickable-element
        class={`cta-wrapper ${ctaType}-cta`}
        element={{ field: fieldMapping[ctaType] }}
        overlayState={state}
        overlayStore={store}
      >
        <button
          type="button"
          class="notification-cta"
          style={{
            '--cta-bg-color': backgroundColour || (ctaType === 'primary' ? '#000000' : '#ffffff'),
            '--cta-border-radius': borderRadius || '0',
            '--cta-border-color': borderColour || 'transparent',
            '--cta-border-width': borderWidth || '0',
          }}
          onClick={() => this.handleCtaClick(ctaType, ctaData)}
          innerHTML={ctaHtml}
        />
      </om-clickable-element>
    );
  }

  render() {
    if (!this.screenValues) return null;

    const { cta, secondCta, thirdCta, hasSecondCta, hasThirdCta } = this.screenValues;
    const showSecondCta = secondCta && (hasSecondCta ? getViewportValue(hasSecondCta) !== false : true);
    const showThirdCta = thirdCta && (hasThirdCta ? getViewportValue(hasThirdCta) !== false : true);

    return (
      <Host
        style={{
          ['--textBlockBgColour']: getViewportValue(this.screenValues.textBlockBgColour, 'colour'),
        }}
      >
        <div
          class={state.isMobileViewport ? 'screen-container is-mobile-viewport' : 'screen-container'}
          onClick={this.layoutContainerClicked.emit}
        >
          <div class="content-area">
            <div class="content-inner">
              <div class="copy-container">
                <om-clickable-element
                  innerHTML={getViewportValue(this.screenValues.title)}
                  class="title"
                  element={{ field: 'title' }}
                  overlayState={state}
                  overlayStore={store}
                />
                <om-clickable-element
                  innerHTML={getViewportValue(this.screenValues.body)}
                  class="body"
                  element={{ field: 'body' }}
                  overlayState={state}
                  overlayStore={store}
                />
              </div>
              {(cta || showSecondCta || showThirdCta) && (
                <div class="cta-section">
                  {cta && this.renderCta(cta, 'primary')}
                  {showSecondCta && this.renderCta(secondCta, 'secondary')}
                  {showSecondCta && showThirdCta && this.renderCta(thirdCta, 'tertiary')}
                </div>
              )}
            </div>
          </div>
        </div>
      </Host>
    );
  }
}
